// External libraries
import React, { useCallback, useEffect } from 'react';

// Zocdoc libraries
import { LOGIN_STATE } from '@zocdoc-frontend-common/state-user-account/constants';
import { EVENT_INITIATORS, INTERACTION_TYPES } from '@zocdoc/frontend-metrics';

// BWA stuff
import useWarningOnUnload, {
    removeUnloadListener,
} from '../../utils/useWarningOnUnload';
import logToDataDog from '../../../config/logToDataDog';
import PatientInfoPageView from './PatientInfoPageView';

const PatientInfoPageContainer = ({
    appleLoginUrl,
    facebookLoginUrl,
    googleLoginUrl,
    loggedInUser,
    onClickAppleLogin,
    onClickFacebookLogin,
    onClickGoogleLogin,
    openLoginModal,
    onSignInSuccess,
    sawLoginModal,
    sendMetric,
    ...viewOnlyProps
}) => {
    // Trigger the popup saying input will be lost when the user refreshes the page
    useWarningOnUnload();

    // Metrics that should only fire once
    useEffect(() => {
        // Datadog metric for prelaunch
        logToDataDog({
            name: 'booking-web-app.saw_patient_info',
            type: 'increment',
            value: 1,
        });

        sendMetric({
            action: 'Loaded patient info view',
            initiator: EVENT_INITIATORS.SYSTEM,
            interactionType: INTERACTION_TYPES.LOAD,
        });
    }, [sendMetric]);

    // Login modal on load for soft-login users
    useEffect(() => {
        const isSoftLoggedIn =
            parseInt(loggedInUser?.login_state, 10) ===
            LOGIN_STATE.SoftLoggedIn;

        if (isSoftLoggedIn && !sawLoginModal) {
            openLoginModal();
        }
    }, [loggedInUser, openLoginModal, sawLoginModal]);

    const onClickLoginLink = useCallback(() => {
        sendMetric({
            action: 'Clicked login link',
            component: 'Login link',
            element: 'Login link',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
        openLoginModal();
    }, [openLoginModal, sendMetric]);

    const handleClickAppleLogin = useCallback(() => {
        removeUnloadListener();
        onClickAppleLogin();
    }, [onClickAppleLogin]);

    const handleClickFacebookLogin = useCallback(() => {
        removeUnloadListener();
        onClickFacebookLogin();
    }, [onClickFacebookLogin]);

    const handleClickGoogleLogin = useCallback(() => {
        removeUnloadListener();
        onClickGoogleLogin();
    }, [onClickGoogleLogin]);

    const viewProps = {
        appleLoginUrl,
        facebookLoginUrl,
        googleLoginUrl,
        handleClickAppleLogin,
        handleClickFacebookLogin,
        handleClickGoogleLogin,
        loggedInUser,
        onClickLoginLink,
        onSignInSuccess,
        ...viewOnlyProps,
    };

    return <PatientInfoPageView {...viewProps} />;
};

export default PatientInfoPageContainer;
